// IndividualPermissions — Fase 3.2 (Feb 2026).
// Owner-only, human-readable 3-state UI on top of the backend privacy_overrides
// + invite_permission_override model from Fase 3. No JSON, no raw permission
// names surface here.
//
// State model:
//   "default"  → no override; server derives from role default
//   "allow"    → explicit allow (True)
//   "deny"     → explicit deny (False)
//
// Field-level saves use PATCH /api/users/{id}. Switching to a different user
// re-fetches the user row from GET /api/users/{id} to avoid stale form state.
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { toast } from "sonner";

const FIELDS = [
  { key: "phone",         labelKey: "privacy_field_phone" },
  { key: "email",         labelKey: "privacy_field_email" },
  { key: "date_of_birth", labelKey: "privacy_field_dob"   },
  { key: "job_title",     labelKey: "privacy_field_title" },
  { key: "department",    labelKey: "privacy_field_dep"   },
  { key: "avatar_url",    labelKey: "privacy_field_photo" },
];

function stateFromOverride(v) {
  if (v === true) return "allow";
  if (v === false) return "deny";
  return "default";
}
function overrideFromState(s) {
  if (s === "allow") return true;
  if (s === "deny")  return false;
  return null; // remove
}

/**
 * @param {{ user: any, companySettings: any, onSaved?: (nextUser:any)=>void }} props
 */
export default function IndividualPermissions({ user: subject, companySettings, onSaved }) {
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const isOwner = currentUser?.role === "owner";
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Local UI state, always seeded from `subject`.
  const [ov, setOv] = useState(() => normaliseOverrides(subject));

  // Re-seed when switching to another person to prevent stale-state leakage.
  // We deliberately only depend on the id — reading the full `subject` in the
  // effect body is intentional so other property tweaks don't reset local UI.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setOv(normaliseOverrides(subject)); setSaveError(null); }, [subject?._id]);

  const targetRole = subject?.role;

  // Role defaults — from company settings (falls back to true if not set).
  const roleDefaults = useMemo(() => {
    const matrix = (companySettings?.privacy_matrix || {})[targetRole] || {};
    const map = {};
    for (const f of FIELDS) map[f.key] = f.key in matrix ? !!matrix[f.key] : true;
    return map;
  }, [companySettings, targetRole]);

  // Invite role default.
  const inviteRoleDefault = useMemo(() => {
    if (targetRole === "manager")  return companySettings?.allow_managers_to_invite !== false;
    if (targetRole === "teamlead") return !!companySettings?.allow_teamleads_to_invite;
    return false;
  }, [companySettings, targetRole]);

  // Only owner may see these controls. Manager can view read-only if we ever
  // decide to expose it, but Fase 3.2 defaults to hiding for non-owner.
  if (!isOwner) return null;
  if (!["manager", "teamlead"].includes(targetRole)) return null;

  const persist = async (nextPrivacy, nextInvite) => {
    setSaving(true);
    setSaveError(null);
    const optimistic = { ...ov, privacy: nextPrivacy, invite: nextInvite };
    setOv(optimistic);
    try {
      const body = {
        privacy_overrides: toBackendPrivacyOverrides(nextPrivacy, targetRole),
        invite_permission_override: nextInvite === "default" ? null : overrideFromState(nextInvite),
      };
      const r = await api.patch(`/users/${subject._id}`, body);
      onSaved?.(r?.data);
      toast.success(t("saved"));
    } catch (err) {
      // Roll UI state back to what the server actually holds.
      setSaveError(err?.response?.data?.detail?.message || t("error"));
      // Reseed from a fresh GET so we don't guess.
      try {
        const fresh = await api.get(`/users/${subject._id}`);
        setOv(normaliseOverrides(fresh.data));
        onSaved?.(fresh.data);
      } catch {}
      toast.error(t("error"));
    } finally {
      setSaving(false);
    }
  };

  const setPrivacyState = (field, next) => {
    const nextPrivacy = { ...ov.privacy, [field]: next };
    persist(nextPrivacy, ov.invite);
  };
  const setInviteState = (next) => persist(ov.privacy, next);
  const resetAll = () => {
    const zero = { phone: "default", email: "default", date_of_birth: "default",
                   job_title: "default", department: "default", avatar_url: "default" };
    persist(zero, "default");
  };

  return (
    <div className="mt-6" data-testid="individual-permissions">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[13px] font-bold text-[hsl(var(--text-strong))]">{t("indiv_perms_title")}</div>
        <button
          type="button"
          onClick={resetAll}
          disabled={saving}
          data-testid="indiv-perms-reset"
          className="text-[11.5px] inline-flex items-center gap-1 px-2 py-1 rounded-full hairline hover:bg-[hsl(var(--muted))]"
        ><RotateCcw size={11} strokeWidth={2} /> {t("indiv_perms_reset")}</button>
      </div>
      <div className="text-[11.5px] text-[hsl(var(--text-muted))] mb-3">{t("indiv_perms_desc")}</div>

      {saveError && (
        <div className="mb-3 p-2.5 rounded-xl bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] text-[12px] font-medium" data-testid="indiv-perms-error">
          {saveError}
        </div>
      )}

      <div className="rounded-2xl border hairline overflow-hidden">
        {FIELDS.map((f) => (
          <PermRow
            key={f.key}
            testid={`indiv-priv-${f.key}`}
            label={t(f.labelKey)}
            defaultAllowed={roleDefaults[f.key]}
            value={ov.privacy[f.key] || "default"}
            onChange={(v) => setPrivacyState(f.key, v)}
            disabled={saving}
            t={t}
          />
        ))}
        {/* Invite override row */}
        <PermRow
          testid="indiv-invite"
          label={t("indiv_perms_invite_label")}
          defaultAllowed={inviteRoleDefault}
          value={ov.invite}
          onChange={setInviteState}
          disabled={saving}
          t={t}
          isInvite
        />
      </div>

      {saving && (
        <div className="mt-2 text-[11px] text-[hsl(var(--text-muted))] inline-flex items-center gap-1.5" data-testid="indiv-perms-saving">
          <Loader2 size={11} className="animate-spin" /> {t("saving")}
        </div>
      )}
    </div>
  );
}

function PermRow({ testid, label, defaultAllowed, value, onChange, disabled, t, isInvite }) {
  const options = [
    { v: "default", labelKey: "indiv_perms_state_default" },
    { v: "allow",   labelKey: "indiv_perms_state_allow"   },
    { v: "deny",    labelKey: "indiv_perms_state_deny"    },
  ];
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-4 py-3 border-b hairline last:border-b-0 bg-[hsl(var(--surface))]" data-testid={testid}>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{label}</div>
        <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">
          {t("indiv_perms_role_default")}: <span className="font-semibold">
            {defaultAllowed ? t("indiv_perms_state_allow") : t("indiv_perms_state_deny")}
          </span>
        </div>
      </div>
      <div role="tablist" className="inline-flex p-1 rounded-full bg-[hsl(var(--muted))] shrink-0" data-testid={`${testid}-group`}>
        {options.map((o) => (
          <motion.button
            key={o.v}
            type="button"
            role="tab"
            aria-selected={value === o.v}
            data-testid={`${testid}-${o.v}`}
            disabled={disabled}
            onClick={() => onChange(o.v)}
            className={`h-7 px-3 rounded-full text-[11.5px] font-semibold transition-colors ${
              value === o.v
                ? "bg-[hsl(var(--surface))] text-[hsl(var(--text-strong))] shadow-[var(--shadow-sm)]"
                : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
            }`}
          >{t(o.labelKey)}</motion.button>
        ))}
      </div>
      {isInvite && null}
    </div>
  );
}

function normaliseOverrides(user) {
  const rp = ((user?.privacy_overrides || {})[user?.role] || {});
  return {
    privacy: {
      phone:         stateFromOverride(rp.phone),
      email:         stateFromOverride(rp.email),
      date_of_birth: stateFromOverride(rp.date_of_birth),
      job_title:     stateFromOverride(rp.job_title),
      department:    stateFromOverride(rp.department),
      avatar_url:    stateFromOverride(rp.avatar_url),
    },
    invite: user?.invite_permission_override === true ? "allow"
          : user?.invite_permission_override === false ? "deny" : "default",
  };
}
function toBackendPrivacyOverrides(nextPrivacy, targetRole) {
  const roleMap = {};
  for (const [k, s] of Object.entries(nextPrivacy)) {
    if (s === "default") continue; // remove
    roleMap[k] = s === "allow";
  }
  // Wrap by role. Backend stores per-role because someone else might view.
  return { [targetRole]: roleMap };
}
